<template>
  <div class="import-report-page">
    <!-- Page Header Section -->
    <div class="page-header">
      <!-- Back link -->
      <router-link
        :to="`/lists/manage-contacts/${id}`"
        class="back-link"
      >
        <i class="fas fa-arrow-left" />
        <span>{{ t('myLists.manage.importReports.backToList') }}</span>
      </router-link>

      <!-- Title row -->
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            {{ t('myLists.manage.importReports.title') }}
          </h1>
          <span
            v-if="listName"
            class="list-name-badge"
          >{{ listName }}</span>
        </div>
      </div>
    </div>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <div class="ImportReports">
        <SpmTableAPI
          v-if="importReports.length > 0"
          ref="childComponentRef"
          mode="client"
          :data="importReports"
          :columns="columns"
          :show-reload-button="true"
          data-key="import_job_id"
          :initial-sort="{ field: 'created_at', order: 'desc' }"
        >
          <!-- Status column -->
          <template #cell-status="{ data }">
            <span
              class="status-badge"
              :class="getStatusClass(data.status)"
            >
              {{ t(`myLists.manage.importReports.statuses.${data.status}`) }}
            </span>
          </template>

          <!-- Total column with progress display -->
          <template #cell-total="{ data }">
            <div class="import-stats">
              <div class="stats-row">
                <span class="stats-total">{{ data.total_rows }} {{ t('myLists.manage.importReports.stats.rows') }}</span>
              </div>
              <div class="stats-details">
                <span class="stats-success">
                  <i class="fas fa-check-circle" />
                  {{ data.processed_rows }} ({{ getPercentage(data.processed_rows, data.total_rows) }}%)
                </span>
                <span
                  v-if="data.error_count > 0"
                  class="stats-error"
                >
                  <i class="fas fa-times-circle" />
                  {{ data.error_count }} ({{ getPercentage(data.error_count, data.total_rows) }}%)
                </span>
              </div>
            </div>
          </template>

          <!-- Date column -->
          <template #cell-created_at="{ data }">
            <span>{{ formatDateDisplay(data.created_at) }}</span>
          </template>

          <!-- Download column -->
          <template #cell-download="{ data }">
            <div class="download-buttons">
              <button
                class="download-btn download-btn--view"
                :title="t('myLists.manage.importReports.viewer.open')"
                @click="openReport(data)"
              >
                <i class="fas fa-eye" />
              </button>
              <button
                class="download-btn download-btn--file"
                :title="t('myLists.manage.importReports.download.file')"
                @click="downloadFile(data.import_job_id, 'file')"
              >
                <i class="fas fa-file-download" />
              </button>
              <button
                v-if="data.error_count > 0"
                class="download-btn download-btn--errors"
                :title="t('myLists.manage.importReports.download.errors')"
                @click="downloadFile(data.import_job_id, 'errors')"
              >
                <i class="fas fa-exclamation-triangle" />
              </button>
            </div>
          </template>
        </SpmTableAPI>

        <!-- Report viewer: the CSV report rendered as a paginated table -->
        <Dialog
          v-model:visible="viewerVisible"
          :header="t('myLists.manage.importReports.viewer.title')"
          :modal="true"
          :dismissable-mask="true"
          :style="{ width: '70rem', maxWidth: '95vw' }"
        >
          <div class="report-viewer">
            <div class="report-viewer__toolbar">
              <div class="report-viewer__tabs">
                <button
                  v-for="option in viewerTypeOptions"
                  :key="option.value"
                  type="button"
                  class="report-viewer__tab"
                  :class="{ 'is-active': viewerType === option.value }"
                  @click="selectViewerType(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
              <button
                v-if="viewerType === 'errors'"
                class="report-viewer__download"
                @click="downloadFile(viewerJobId, 'errors')"
              >
                <i class="fas fa-file-csv" />
                <span>{{ t('myLists.manage.importReports.download.errors') }}</span>
              </button>
            </div>

            <DataTable
              :value="viewerRows"
              :lazy="true"
              :loading="viewerLoading"
              :paginator="true"
              :rows="viewerLimit"
              :total-records="viewerTotal"
              :first="(viewerPage - 1) * viewerLimit"
              :rows-per-page-options="[25, 50, 100]"
              data-key="line_number"
              class="report-viewer__table"
              @page="onViewerPage"
            >
              <template #empty>
                <div class="report-viewer__empty">
                  {{ t(`myLists.manage.importReports.viewer.empty.${viewerType === 'errors' ? 'errors' : 'all'}`) }}
                </div>
              </template>

              <Column
                field="line_number"
                :header="t('myLists.manage.importReports.viewer.columns.line_number')"
                :style="{ width: '6rem' }"
              />
              <Column
                v-if="viewerType === 'errors'"
                field="error_code"
                :header="t('myLists.manage.importReports.viewer.columns.error_code')"
                :style="{ width: '22rem' }"
              >
                <template #body="{ data }">
                  <span class="report-viewer__reason">
                    <i class="fas fa-circle-exclamation" />
                    {{ errorCodeLabel(data.error_code) }}
                  </span>
                </template>
              </Column>
              <Column
                v-if="viewerType === 'errors'"
                field="raw_line"
                :header="t('myLists.manage.importReports.viewer.columns.raw_line')"
              >
                <template #body="{ data }">
                  <span class="report-viewer__raw">{{ data.raw_line }}</span>
                </template>
              </Column>
              <Column
                v-else
                field="action"
                :header="t('myLists.manage.importReports.viewer.columns.action')"
              >
                <template #body="{ data }">
                  {{ actionLabel(data.action) }}
                </template>
              </Column>
            </DataTable>
          </div>
        </Dialog>

        <!-- Empty state when no data -->
        <div
          v-if="importReports.length === 0 && !isLoading"
          class="empty-state"
        >
          <i class="fas fa-file-alt empty-icon" />
          <p>{{ t('myLists.manage.importReports.noReports') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import type {
  SpmColumnConfig,
} from '@/components/table-rest-api/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError } from '@/helpers';
import {
  computed,
  defineComponent, onBeforeMount, ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { UserState } from '@/composables/User';

type ReportViewerType = 'errors' | 'report';

interface ReportRow {
  line_number?: string;
  error_code?: string;
  raw_line?: string;
  action?: string;
}

export default defineComponent({
  name: 'ImportReportContacts',

  components: {
    SpmTableAPI,
    Dialog,
    DataTable,
    Column,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup(props: { id: string }) {
    const { t } = useI18n();
    const router = useRouter();
    const childComponentRef = ref();
    const listName = ref('');
    const importReports = ref<any[]>([]);
    const isLoading = ref(false);

    const columns = computed<SpmColumnConfig[]>(() => [
      {
        id: 'file_name',
        header: t('myLists.manage.importReports.headers.filename'),
        fields: [{
          name: 'file_name',
          label: t('myLists.manage.importReports.headers.filename'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'status',
        header: t('myLists.manage.importReports.headers.status'),
        fields: [{
          name: 'status',
          label: t('myLists.manage.importReports.headers.status'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { value: 'pending', label: t('myLists.manage.importReports.statuses.pending') },
              { value: 'processing', label: t('myLists.manage.importReports.statuses.processing') },
              { value: 'completed', label: t('myLists.manage.importReports.statuses.completed') },
              { value: 'failed', label: t('myLists.manage.importReports.statuses.failed') },
            ],
          },
        }],
      },
      {
        id: 'total',
        header: t('myLists.manage.importReports.headers.total'),
        fields: ['total_rows', 'processed_rows', 'error_count'],
      },
      {
        id: 'created_at',
        header: t('myLists.manage.importReports.headers.createdAt'),
        fields: [{
          name: 'created_at',
          label: t('myLists.manage.importReports.headers.createdAt'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'download',
        header: t('myLists.manage.importReports.headers.download'),
        fields: ['import_job_id', 'error_count'],
      },
    ]);

    /**
     * Fetch list details to verify the list exists
     */
    const fetchListDetails = async () => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/lists/${props.id}`,
          {},
          '',
          undefined,
          true,
          { fields: ['name', 'type'] },
        );

        if (response && response.statusCode === 200 && response.data.name) {
          listName.value = response.data.name;
        } else {
          showToastError(t('myLists.manage.importReports.errors.listNotFound'));
          router.push('/lists/manage');
        }
      } catch (error) {
        console.error('Error fetching list details:', error);
        showToastError(t('myLists.manage.importReports.errors.listNotFound'));
        router.push('/lists/manage');
      }
    };

    /**
     * Fetch import reports from the IMPORT API
     */
    const fetchImportReports = async () => {
      isLoading.value = true;
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'post',
          NestApiType.IMPORT,
          '/imports/list',
          {},
          '',
          {
            // PermissionGuard côté API contrôle la boutique sur `idShop`.
            idShop,
            id_shop_list: props.id,
          },
          true,
        );

        if (response && Array.isArray(response)) {
          importReports.value = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          importReports.value = response.data;
        } else {
          importReports.value = [];
        }
      } catch (error) {
        console.error('Error fetching import reports:', error);
        importReports.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    // ==========================================================================
    // REPORT VIEWER — le rapport CSV affiché en tableau paginé
    // ==========================================================================

    const viewerVisible = ref(false);
    const viewerJobId = ref(0);
    const viewerType = ref<ReportViewerType>('errors');
    const viewerRows = ref<ReportRow[]>([]);
    const viewerTotal = ref(0);
    const viewerPage = ref(1);
    const viewerLimit = ref(25);
    const viewerLoading = ref(false);

    const viewerTypeOptions = computed<Array<{ value: ReportViewerType; label: string }>>(() => [
      { value: 'errors', label: t('myLists.manage.importReports.viewer.tabs.errors') },
      { value: 'report', label: t('myLists.manage.importReports.viewer.tabs.all') },
    ]);

    /**
     * Traduit un code d'erreur du rapport ; un code inconnu reste affiché tel
     * quel plutôt que de laisser une cellule vide.
     */
    const errorCodeLabel = (code: string): string => {
      if (!code) return '';
      const key = `myLists.manage.importReports.errorCodes.${code}`;
      const label = t(key);
      return label === key ? code : label;
    };

    const actionLabel = (action: string): string => {
      if (!action) return '';
      const key = `myLists.manage.importReports.actions.${action}`;
      const label = t(key);
      return label === key ? action : label;
    };

    /**
     * Charge une page du rapport auprès de l'API d'import.
     */
    const fetchReportRows = async () => {
      if (!viewerJobId.value) return;
      viewerLoading.value = true;
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'get',
          NestApiType.IMPORT,
          `/imports/report/${idShop}/${viewerJobId.value}`,
          {},
          '',
          undefined,
          true,
          {
            type: viewerType.value,
            page: viewerPage.value,
            limit: viewerLimit.value,
          },
        );

        // nestCall n'émet pas d'exception sur une réponse en erreur : sans ce
        // contrôle, un rapport illisible s'afficherait comme un import sans
        // erreur, ce qui est faux et trompeur.
        if (response?.error || !Array.isArray(response?.rows)) {
          throw new Error('Invalid import report response');
        }

        viewerRows.value = response.rows;
        viewerTotal.value = Number(response.total ?? 0);
      } catch (error) {
        console.error('Error fetching import report rows:', error);
        viewerRows.value = [];
        viewerTotal.value = 0;
        showToastError(t('myLists.manage.importReports.viewer.loadError'));
      } finally {
        viewerLoading.value = false;
      }
    };

    const openReport = async (report: { import_job_id: number; error_count: number }) => {
      viewerJobId.value = report.import_job_id;
      // On ouvre sur les erreurs quand il y en a, sinon sur le détail complet.
      viewerType.value = report.error_count > 0 ? 'errors' : 'report';
      viewerPage.value = 1;
      viewerRows.value = [];
      viewerTotal.value = 0;
      viewerVisible.value = true;
      await fetchReportRows();
    };

    const onViewerPage = async (event: { page: number; rows: number }) => {
      viewerPage.value = event.page + 1;
      viewerLimit.value = event.rows;
      await fetchReportRows();
    };

    /** Changer d'onglet repart de la première page. */
    const selectViewerType = async (type: ReportViewerType) => {
      if (viewerType.value === type) return;
      viewerType.value = type;
      viewerPage.value = 1;
      await fetchReportRows();
    };

    /**
     * Calculate percentage
     */
    const getPercentage = (value: number, total: number): string => {
      if (!total || total === 0) return '0';
      return Math.round((value / total) * 100).toString();
    };

    /**
     * Download file (import file or error report)
     */
    const downloadFile = async (importJobId: number, type: 'file' | 'errors') => {
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const response = await nestCall(
          'get',
          NestApiType.IMPORT,
          `/imports/download/${idShop}/${importJobId}`,
          {},
          '',
          undefined,
          true,
          { type },
        );
        if (response) {
          // If response contains file data, create a blob and download
          const blob = new Blob([response], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `import_${importJobId}_${type}.txt`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    };

    onBeforeMount(async () => {
      await fetchListDetails();
      await fetchImportReports();
    });

    /**
     * Format date for display
     */
    const formatDateDisplay = (dateString: string) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    };

    /**
     * Get CSS class for status badge
     */
    const getStatusClass = (status: string): string => {
      switch (status) {
        case 'completed':
          return 'status-completed';
        case 'processing':
          return 'status-processing';
        case 'pending':
          return 'status-pending';
        case 'failed':
          return 'status-failed';
        default:
          return 'status-pending';
      }
    };

    return {
      t,
      listName,
      childComponentRef,
      importReports,
      isLoading,
      columns,
      formatDateDisplay,
      getStatusClass,
      getPercentage,
      downloadFile,
      viewerVisible,
      viewerJobId,
      viewerType,
      viewerTypeOptions,
      viewerRows,
      viewerTotal,
      viewerPage,
      viewerLimit,
      viewerLoading,
      openReport,
      onViewerPage,
      selectViewerType,
      errorCodeLabel,
      actionLabel,
    };
  },
});
</script>

<style scoped lang="scss">
/* Page layout */
.import-report-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Page Header */
.page-header {
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--secondary-700);
  }

  i {
    font-size: 0.75rem;
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  line-height: 1.2;
}

.list-name-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background-color: var(--surface-100);
  color: var(--text-color-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
}

/* Page Content */
.page-content {
  flex: 1;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
}

.status-completed {
  background-color: #ecfdf5;
  color: #047857;
}

.status-processing {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.status-pending {
  background-color: #fef3c7;
  color: #d97706;
}

.status-failed {
  background-color: #fef2f2;
  color: #dc2626;
}

/* Import stats column */
.import-stats {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stats-row {
  display: flex;
  align-items: center;
}

.stats-total {
  font-weight: 600;
  color: var(--text-color);
}

.stats-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
}

.stats-success {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #047857;

  i {
    font-size: 0.7rem;
  }
}

.stats-error {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #dc2626;

  i {
    font-size: 0.7rem;
  }
}

/* Download buttons */
.download-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 0.875rem;
  }

  &:hover {
    transform: translateY(-1px);
  }
}

.download-btn--view {
  background-color: var(--surface-100);
  color: var(--text-color-secondary);

  &:hover {
    background-color: var(--surface-200);
  }
}

.download-btn--file {
  background-color: #eff6ff;
  color: #1d4ed8;

  &:hover {
    background-color: #dbeafe;
  }
}

/* Report viewer */
.report-viewer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.report-viewer__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.report-viewer__tabs {
  display: inline-flex;
  border: 1px solid var(--surface-300);
  border-radius: 6px;
  overflow: hidden;
}

.report-viewer__tab {
  padding: 0.4rem 0.9rem;
  border: none;
  background-color: var(--surface-0);
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  & + & {
    border-left: 1px solid var(--surface-300);
  }

  &:hover {
    background-color: var(--surface-100);
  }

  &.is-active {
    background-color: var(--primary-color, #94c840);
    color: #fff;
  }
}

.report-viewer__download {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--surface-300);
  border-radius: 6px;
  background-color: var(--surface-0);
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--surface-100);
  }
}

.report-viewer__reason {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #b91c1c;

  i {
    font-size: 0.75rem;
  }
}

.report-viewer__raw {
  font-family: monospace;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
  word-break: break-all;
}

.report-viewer__empty {
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

.download-btn--errors {
  background-color: #fef2f2;
  color: #dc2626;

  &:hover {
    background-color: #fee2e2;
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--text-color-secondary);

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-size: 1rem;
  }
}
</style>
